'use strict';

// ========================================
// REACTIVE CONTACT MESH
// Interactive geometric form network
// ========================================

// Utility: debounce function (scoped to avoid naming collisions)
const meshDebounce = (fn, delay) => {
    let timer = null;
    return (...args) => {
        clearTimeout(timer);
        timer = setTimeout(() => fn(...args), delay);
    };
};

class ContactMesh {
    constructor(canvasId) {
        this.canvas = document.getElementById(canvasId);
        if (!this.canvas) return;

        this.ctx = this.canvas.getContext('2d');
        this.container = this.canvas.parentElement;
        this.mouse = { x: null, y: null };
        this.time = 0;
        this.isActive = true;

        // Form state
        this.formData = {
            nome: { value: '', valid: false, touched: false },
            email: { value: '', valid: false, touched: false },
            messaggio: { value: '', valid: false, touched: false }
        };
        this.activeNode = null;
        this.isSubmitting = false;
        this.submitProgress = 0;
        this.showSuccess = false;

        // Colors from site palette
        this.colors = {
            background: '#040911',
            nodeFill: '#0a1628',
            nodeStroke: '#2D68C4',
            nodeHover: '#00d4ff',
            nodeValid: '#00ff88',
            nodeError: '#ff4466',
            connection: 'rgba(45, 104, 196, 0.3)',
            connectionActive: 'rgba(0, 212, 255, 0.6)',
            text: '#dee8f7',
            textMuted: '#8a9bb5'
        };

        // Refined Node/Connection data
        this.projectNodes = [];
        this.projectConnections = [];

        // Decorative particles
        this.particles = [];

        this.init();
    }

    init() {
        this.resize();
        this.createInputOverlays();
        this.createParticles();
        this.bindEvents();
        this.animate();
    }

    resize() {
        this.canvas.width = this.container.offsetWidth;
        this.canvas.height = this.container.offsetHeight;
        this.updateNodePositions();
        this.createParticles();
    }

    updateNodePositions() {
        const w = this.canvas.width;
        const h = this.canvas.height;

        // Base definitions
        const rawNodes = [
            { id: 'nome', label: 'NOME', px: 0.3, py: 0.35, size: 45, type: 'input' },
            { id: 'email', label: 'EMAIL', px: 0.7, py: 0.35, size: 45, type: 'input' },
            { id: 'messaggio', label: 'MESSAGGIO', px: 0.5, py: 0.55, size: 55, type: 'textarea' },
            { id: 'submit', label: 'TRASMETTI', px: 0.5, py: 0.8, size: 50, type: 'submit' }
        ];

        this.nodes = rawNodes.map(node => ({
            ...node,
            x: w * node.px,
            y: h * node.py
        }));

        const rawConnections = [
            ['nome', 'email'],
            ['nome', 'messaggio'],
            ['email', 'messaggio'],
            ['messaggio', 'submit']
        ];

        this.connections = rawConnections.map(([id1, id2]) => {
            return [
                this.nodes.find(n => n.id === id1),
                this.nodes.find(n => n.id === id2)
            ];
        });
    }

    createParticles() {
        this.particles = [];
        const count = Math.floor((this.canvas.width * this.canvas.height) / 8000);

        for (let i = 0; i < count; i++) {
            this.particles.push({
                x: Math.random() * this.canvas.width,
                y: Math.random() * this.canvas.height,
                size: Math.random() * 2 + 0.5,
                alpha: Math.random() * 0.3 + 0.1,
                phase: Math.random() * Math.PI * 2,
                speed: 0.005 + Math.random() * 0.01
            });
        }
    }

    createInputOverlays() {
        // Create input container
        let inputContainer = this.container.querySelector('.mesh-inputs');
        if (!inputContainer) {
            inputContainer = document.createElement('div');
            inputContainer.className = 'mesh-inputs';
            this.container.appendChild(inputContainer);
        }

        // Create inputs for each node
        this.nodes.forEach(node => {
            if (node.type === 'input' || node.type === 'textarea') {
                const wrapper = document.createElement('div');
                wrapper.className = 'mesh-input-wrapper';
                wrapper.id = `input-${node.id}`;

                if (node.type === 'textarea') {
                    wrapper.innerHTML = `
                        <textarea 
                            placeholder="${this.getPlaceholder(node.id)}"
                            rows="3"
                        ></textarea>
                        <span class="input-close">×</span>
                    `;
                } else {
                    wrapper.innerHTML = `
                        <input 
                            type="${node.id === 'email' ? 'email' : 'text'}" 
                            placeholder="${this.getPlaceholder(node.id)}"
                        >
                        <span class="input-close">×</span>
                    `;
                }

                inputContainer.appendChild(wrapper);

                // Bind input events
                const input = wrapper.querySelector('input, textarea');
                input.addEventListener('input', (e) => this.handleInput(node.id, e.target.value));
                input.addEventListener('blur', () => this.validateField(node.id));

                const closeBtn = wrapper.querySelector('.input-close');
                closeBtn.addEventListener('click', () => this.closeInput(node.id));
            }
        });

        // Create status message element
        let statusEl = this.container.querySelector('.mesh-status');
        if (!statusEl) {
            statusEl = document.createElement('div');
            statusEl.className = 'mesh-status';
            this.container.appendChild(statusEl);
        }
        this.statusEl = statusEl;
    }

    getPlaceholder(id) {
        const placeholders = {
            nome: 'Il tuo nome...',
            email: 'email@domain.com',
            messaggio: 'Descrivi il tuo progetto...'
        };
        return placeholders[id] || '';
    }

    bindEvents() {
        // Debounced resize handler
        const debouncedResize = meshDebounce(() => this.resize(), 150);
        window.addEventListener('resize', debouncedResize);

        this.canvas.addEventListener('mousemove', (e) => {
            const rect = this.canvas.getBoundingClientRect();
            this.mouse.x = e.clientX - rect.left;
            this.mouse.y = e.clientY - rect.top;
        });

        this.canvas.addEventListener('mouseleave', () => {
            this.mouse.x = null;
            this.mouse.y = null;
        });

        this.canvas.addEventListener('click', (e) => {
            if (this.isSubmitting || this.showSuccess) return;

            const rect = this.canvas.getBoundingClientRect();
            const x = e.clientX - rect.left;
            const y = e.clientY - rect.top;

            const clickedNode = this.getNodeAt(x, y);
            if (clickedNode) {
                if (clickedNode.type === 'submit') {
                    this.handleSubmit();
                } else {
                    this.openInput(clickedNode);
                }
            }
        });

        document.addEventListener('visibilitychange', () => {
            const isSectionActive = this.canvas.closest('.section')?.classList.contains('section--is-active') ?? true;
            if (!document.hidden && this.isInViewport && isSectionActive) {
                if (!this.isActive) {
                    this.isActive = true;
                    this.animate();
                }
            } else {
                this.isActive = false;
            }
        });

        // PERFORMANCE: IntersectionObserver - pause when off-screen
        const observer = new IntersectionObserver((entries) => {
            entries.forEach(entry => {
                this.isInViewport = entry.isIntersecting;
                this.checkActiveState();
            });
        }, { threshold: 0.1 });

        observer.observe(this.container);

        // PERFORMANCE: MutationObserver - strict pause when section is inactive
        const sectionEl = this.canvas.closest('.section');
        if (sectionEl) {
            const classObserver = new MutationObserver(() => this.checkActiveState());
            classObserver.observe(sectionEl, { attributes: true, attributeFilter: ['class'] });
        }
    }

    checkActiveState() {
        const sectionEl = this.canvas.closest('.section');
        const isSectionActive = sectionEl ? sectionEl.classList.contains('section--is-active') : true;
        const shouldBeActive = this.isInViewport && !document.hidden && isSectionActive;

        if (shouldBeActive && !this.isActive) {
            this.isActive = true;
            this.animate();
        } else if (!shouldBeActive) {
            this.isActive = false;
        }
    }

    getNodeAt(x, y) {
        for (const node of this.nodes) {
            const distSq = (x - node.x) ** 2 + (y - node.y) ** 2;
            const threshold = (node.size + 10) ** 2;
            if (distSq <= threshold) {
                return node;
            }
        }
        return null;
    }

    getHoveredNode() {
        if (!this.mouse.x) return null;
        return this.getNodeAt(this.mouse.x, this.mouse.y);
    }

    openInput(node) {
        // Close any other open inputs
        this.nodes.forEach(n => {
            if (n.id !== node.id) {
                const wrapper = document.getElementById(`input-${n.id}`);
                if (wrapper) wrapper.classList.remove('active');
            }
        });

        const wrapper = document.getElementById(`input-${node.id}`);
        if (wrapper) {
            wrapper.classList.add('active');

            const input = wrapper.querySelector('input, textarea');
            if (input) {
                setTimeout(() => input.focus(), 100);
            }

            this.activeNode = node.id;
            this.formData[node.id].touched = true;
        }
    }

    closeInput(nodeId) {
        const wrapper = document.getElementById(`input-${nodeId}`);
        if (wrapper) {
            wrapper.classList.remove('active');
        }
        if (this.activeNode === nodeId) {
            this.activeNode = null;
        }
    }

    handleInput(nodeId, value) {
        this.formData[nodeId].value = value;
        this.validateField(nodeId);
    }

    validateField(nodeId) {
        const field = this.formData[nodeId];

        switch (nodeId) {
            case 'nome':
                field.valid = field.value.trim().length >= 2;
                break;
            case 'email':
                field.valid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(field.value);
                break;
            case 'messaggio':
                field.valid = field.value.trim().length >= 10;
                break;
        }

        return field.valid;
    }

    isFormValid() {
        return Object.values(this.formData).every(f => f.valid);
    }

    handleSubmit() {
        // Validate all fields
        Object.keys(this.formData).forEach(id => {
            this.formData[id].touched = true;
            this.validateField(id);
        });

        if (!this.isFormValid()) {
            this.shakeInvalidNodes();
            return;
        }

        // Start submission animation
        this.isSubmitting = true;
        this.submitProgress = 0;
        this.submitStartTime = performance.now();

        // Actually submit the form after animation
        setTimeout(() => {
            this.submitToFormspree();
        }, 1500);
    }

    shakeInvalidNodes() {
        Object.keys(this.formData).forEach(id => {
            if (!this.formData[id].valid && this.formData[id].touched) {
                const wrapper = document.getElementById(`input-${id}`);
                if (wrapper) {
                    wrapper.classList.add('shake');
                    setTimeout(() => wrapper.classList.remove('shake'), 500);
                }
            }
        });
    }

    async submitToFormspree() {
        const formData = new FormData();
        formData.append('nome', this.formData.nome.value);
        formData.append('email', this.formData.email.value);
        formData.append('messaggio', this.formData.messaggio.value);

        try {
            const response = await fetch('https://formspree.io/f/mkndbgya', {
                method: 'POST',
                body: formData,
                headers: { 'Accept': 'application/json' }
            });

            if (response.ok) {
                this.showSuccess = true;
                this.statusEl.textContent = 'Segnale ricevuto.';
                this.statusEl.classList.add('visible', 'success');
            } else {
                throw new Error('Submission failed');
            }
        } catch (error) {
            this.statusEl.textContent = 'Errore di trasmissione.';
            this.statusEl.classList.add('visible', 'error');
            this.isSubmitting = false;
        }
    }

    animate() {
        if (!this.isActive) return;

        this.time += 0.016;

        // Clear canvas
        // Clear with transparency to show global background
        this.ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);

        // Draw particles
        this.drawParticles();

        // Get hovered node
        const hoveredNode = this.getHoveredNode();

        // Update cursor
        this.canvas.style.cursor = hoveredNode ? 'pointer' : 'default';

        // Calculate submission animation
        let convergeFactor = 0;
        if (this.isSubmitting) {
            const elapsed = (performance.now() - this.submitStartTime) / 1500;
            convergeFactor = this.easeInOutCubic(Math.min(elapsed, 1));
        }

        // Draw connections
        this.drawConnections(hoveredNode, convergeFactor);

        // Draw nodes
        this.drawNodes(hoveredNode, convergeFactor);

        // Draw title
        if (!this.showSuccess) {
            this.drawTitle();
        }

        requestAnimationFrame(() => this.animate());
    }

    easeInOutCubic(t) {
        return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
    }

    drawParticles() {
        const ctx = this.ctx;
        this.particles.forEach(p => {
            p.phase += p.speed;
            const twinkle = (Math.sin(p.phase) + 1) * 0.5;
            const alpha = p.alpha * (0.4 + twinkle * 0.6);

            ctx.beginPath();
            ctx.arc(p.x, p.y, p.size, 0, Math.PI * 2);
            ctx.fillStyle = `rgba(45, 104, 196, ${alpha})`;
            ctx.fill();
        });
    }

    drawConnections(hoveredNode, convergeFactor) {
        const ctx = this.ctx;
        const centerX = this.canvas.width / 2;
        const centerY = this.canvas.height / 2;
        const isSubmitting = convergeFactor > 0;

        this.connections.forEach(([n1, n2]) => {
            if (!n1 || !n2) return;

            let x1 = n1.x, y1 = n1.y;
            let x2 = n2.x, y2 = n2.y;

            if (isSubmitting) {
                x1 += (centerX - x1) * convergeFactor;
                y1 += (centerY - y1) * convergeFactor;
                x2 += (centerX - x2) * convergeFactor;
                y2 += (centerY - y2) * convergeFactor;
            }

            const id1 = n1.id, id2 = n2.id;
            const bothValid =
                (this.formData[id1]?.valid || id1 === 'submit') &&
                (this.formData[id2]?.valid || id2 === 'submit');

            let lineAlpha = 0.3;
            let lineWidth = 1;

            if (bothValid) {
                lineAlpha = 0.6;
                lineWidth = 2;
            }

            if (hoveredNode && (hoveredNode.id === id1 || hoveredNode.id === id2)) {
                lineAlpha = 0.8;
            }

            ctx.beginPath();
            ctx.moveTo(x1, y1);
            ctx.lineTo(x2, y2);
            ctx.strokeStyle = bothValid ? this.colors.connectionActive : this.colors.connection;
            ctx.globalAlpha = lineAlpha;
            ctx.lineWidth = lineWidth;
            ctx.stroke();
            ctx.globalAlpha = 1;
        });
    }

    drawNodes(hoveredNode, convergeFactor) {
        const ctx = this.ctx;
        const centerX = this.canvas.width / 2;
        const centerY = this.canvas.height / 2;
        const isMobileSmall = this.canvas.width < 550;
        const sizeScale = isMobileSmall ? 0.75 : 1;
        const isSubmitting = convergeFactor > 0;

        this.nodes.forEach((node, index) => {
            let x = node.x;
            let y = node.y;

            if (isSubmitting) {
                x += (centerX - x) * convergeFactor;
                y += (centerY - y) * convergeFactor;
            }

            const isHovered = hoveredNode?.id === node.id;
            const isActive = this.activeNode === node.id;
            const fieldData = this.formData[node.id];

            let strokeColor = this.colors.nodeStroke;
            let glowColor = 'rgba(45, 104, 196, 0.3)';

            if (node.type === 'submit') {
                if (this.isFormValid()) {
                    strokeColor = this.colors.nodeValid;
                    glowColor = 'rgba(0, 255, 136, 0.3)';
                }
            } else if (fieldData) {
                if (fieldData.valid) {
                    strokeColor = this.colors.nodeValid;
                    glowColor = 'rgba(0, 255, 136, 0.3)';
                } else if (fieldData.touched && !fieldData.valid) {
                    strokeColor = this.colors.nodeError;
                    glowColor = 'rgba(255, 68, 102, 0.3)';
                }
            }

            if (isHovered || isActive) {
                strokeColor = this.colors.nodeHover;
                glowColor = 'rgba(0, 212, 255, 0.4)';
            }

            const pulse = Math.sin(this.time * 2 + index) * 0.1 + 1;
            const size = node.size * pulse * sizeScale;

            const gradient = ctx.createRadialGradient(x, y, 0, x, y, size * 2);
            gradient.addColorStop(0, glowColor);
            gradient.addColorStop(1, 'rgba(0, 0, 0, 0)');
            ctx.beginPath();
            ctx.arc(x, y, size * 2, 0, Math.PI * 2);
            ctx.fillStyle = gradient;
            ctx.fill();

            ctx.beginPath();
            ctx.arc(x, y, size, 0, Math.PI * 2);
            ctx.fillStyle = this.colors.nodeFill;
            ctx.fill();
            ctx.strokeStyle = strokeColor;
            ctx.lineWidth = isHovered ? 3 : 2;
            ctx.stroke();

            ctx.beginPath();
            ctx.arc(x, y, size * 0.6, 0, Math.PI * 2);
            ctx.strokeStyle = strokeColor;
            ctx.lineWidth = 1;
            ctx.globalAlpha = 0.5;
            ctx.stroke();
            ctx.globalAlpha = 1;

            ctx.font = '12px "Roundor", monospace';
            ctx.fillStyle = this.colors.text;
            ctx.textAlign = 'center';
            if (!isHovered && !isActive) {
                ctx.globalAlpha = 0.5;
            }
            ctx.fillText(node.label, x, y - size - 15);
            ctx.globalAlpha = 1;
        });

        if (convergeFactor > 0.5) {
            const pulseSize = 100 * (convergeFactor - 0.5) * 2;
            const alpha = 1 - (convergeFactor - 0.5) * 2;

            ctx.beginPath();
            ctx.arc(centerX, centerY, pulseSize, 0, Math.PI * 2);
            ctx.strokeStyle = this.colors.nodeHover;
            ctx.lineWidth = 3;
            ctx.globalAlpha = alpha;
            ctx.stroke();
            ctx.globalAlpha = 1;
        }
    }

    drawTitle() {
        this.ctx.font = '24px "Roundor", sans-serif';
        this.ctx.fillStyle = this.colors.textMuted;
        this.ctx.textAlign = 'center';
        this.ctx.globalAlpha = 0.6;
        this.ctx.fillText('CONNETTITI', this.canvas.width / 2, 100);
        this.ctx.globalAlpha = 1;

        // Prompt removed to clean up interface
    }
}

// Initialize on DOMContentLoaded
document.addEventListener('DOMContentLoaded', () => {
    setTimeout(() => {
        new ContactMesh('contact-mesh-canvas');
    }, 100);
});

// Reinitialize when navigating to section
$(document).ready(function () {
    $(document).on('click', '.side-nav li:nth-child(5), .outer-nav li:nth-child(5)', function () {
        setTimeout(() => {
            const canvas = document.getElementById('contact-mesh-canvas');
            if (canvas && !canvas.hasAttribute('data-init')) {
                new ContactMesh('contact-mesh-canvas');
                canvas.setAttribute('data-init', 'true');
            }
        }, 500);
    });
});
